import { useState, useEffect } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { SearchResults, CountyResearchRecord } from "./types";
import { RecordDetailModal } from "./RecordDetailModal";
import { formatCurrency } from "@/components/investor/utils";
import { format } from "date-fns";
import { ExternalLink, Eye, RefreshCw, Trash2, Download, ChevronLeft, ChevronRight, ChevronDown, Save, X, Upload, Camera } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import * as XLSX from 'xlsx';

interface ResearchResultsProps {
  searchResults: SearchResults;
  onRefresh: () => void;
  defaultCountyFilter?: string;
}

export function ResearchResults({ searchResults, defaultCountyFilter }: ResearchResultsProps) {
  const [selectedRecord, setSelectedRecord] = useState<CountyResearchRecord | null>(null);
  const [records, setRecords] = useState<CountyResearchRecord[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [isReviewMode, setIsReviewMode] = useState(false);
  const [reviewIndex, setReviewIndex] = useState<number | null>(null);
  const [manualAddress, setManualAddress] = useState('');
  const [spreadsheetId, setSpreadsheetId] = useState("");
  const [isSavingToSheets, setIsSavingToSheets] = useState(false);
  const [countyFilter, setCountyFilter] = useState<string>(defaultCountyFilter || "all");
  const [statusFilter, setStatusFilter] = useState<string>("all");
  const [capturingRecordId, setCapturingRecordId] = useState<string | null>(null);
  const [uploadingRecordId, setUploadingRecordId] = useState<string | null>(null);
  const fileInputRef = useState<{ [key: string]: HTMLInputElement | null }>({})[0];
  const [expandedRows, setExpandedRows] = useState<Set<string>>(new Set());

  const allRecords = records.length > 0 ? records : searchResults.records;
  
  let displayRecords = allRecords;
  
  // Apply county filter
  if (countyFilter !== "all") {
    displayRecords = displayRecords.filter(r => r.county?.toLowerCase() === countyFilter.toLowerCase());
  }
  
  // Apply status filter
  if (statusFilter === "melissa") {
    displayRecords = displayRecords.filter(r => r.status === 'ready_for_melissa');
  } else if (statusFilter === "needs_review") {
    displayRecords = displayRecords.filter(r => r.address_needs_review);
  }
  
  const recordsNeedingReview = displayRecords.filter(r => r.address_needs_review);
  const comalRecordsNeedingReview = displayRecords.filter(r => r.county === 'comal' && !r.grantor_address);
  const recordsReadyForMelissa = allRecords.filter(r => r.status === 'ready_for_melissa');
  const counties = Array.from(new Set(allRecords.map(r => r.county).filter(Boolean)));
  
  // Determine if we're in Comal review mode based on current record
  const isComalReviewMode = reviewIndex !== null && displayRecords[reviewIndex]?.county === 'comal';
  const activeReviewList = isComalReviewMode ? comalRecordsNeedingReview : recordsNeedingReview;
  
  // Build review record numbers based on the active review mode
  const reviewRecordNumbers = displayRecords
    .map((r, idx) => {
      if (isComalReviewMode) {
        return (r.county === 'comal' && !r.grantor_address) ? idx : -1;
      } else {
        return r.address_needs_review ? idx : -1;
      }
    })
    .filter(idx => idx !== -1);
  const currentReviewPosition = reviewIndex !== null ? reviewRecordNumbers.indexOf(reviewIndex) + 1 : 0;

  const loadRecords = async () => {
    setIsLoading(true);
    try {
      const { data, error } = await supabase
        .from('county_research')
        .select('*')
        .is('deleted_at', null) // Only load non-deleted records
        .order('filing_date', { ascending: true })
        .order('created_at', { ascending: true });

      if (error) throw error;

      setRecords((data || []) as CountyResearchRecord[]);
    } catch (error) {
      console.error("Error loading records:", error);
      toast.error("Failed to load records");
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    loadRecords();
  }, []);

  const handleDelete = async (recordId: string) => {
    if (!confirm("Are you sure you want to delete this record? It will stay deleted permanently.")) {
      return;
    }

    try {
      // Soft delete - set deleted_at timestamp instead of removing record
      const { error } = await supabase
        .from('county_research')
        .update({ deleted_at: new Date().toISOString() })
        .eq('id', recordId);

      if (error) throw error;

      toast.success("Record deleted successfully");
      setSelectedIds(new Set());
      loadRecords();
    } catch (error) {
      console.error("Error deleting record:", error);
      toast.error("Failed to delete record");
    }
  };

  const handleManualScreenshotUpload = async (record: CountyResearchRecord, file: File) => {
    setUploadingRecordId(record.id);
    toast.info("📤 Uploading screenshot and extracting address...", { duration: 3000 });

    try {
      // Convert file to base64
      const reader = new FileReader();
      const base64Promise = new Promise<string>((resolve, reject) => {
        reader.onload = () => {
          const base64 = reader.result as string;
          resolve(base64.split(',')[1]); // Remove data:image/...;base64, prefix
        };
        reader.onerror = reject;
      });
      reader.readAsDataURL(file);
      
      const base64Image = await base64Promise;

      // Call edge function to extract address from the screenshot
      const { data, error } = await supabase.functions.invoke('extract-seller-address', {
        body: { 
          recordId: record.id,
          screenshotBase64: base64Image
        }
      });

      if (error) throw error;

      if (data.success && data.address) {
        toast.success(`✅ Address extracted (${data.confidence}% confidence) - Click Save & Next to confirm`, { duration: 4000 });
        // Update the manual address field in review mode
        setManualAddress(data.address);
        // Don't auto-save - let user click "Save & Next" button
      } else {
        toast.error("Failed to extract address from screenshot");
      }
    } catch (error) {
      console.error("Error uploading screenshot:", error);
      toast.error("Failed to process screenshot");
    } finally {
      setUploadingRecordId(null);
    }
  };

  const handlePasteScreenshot = async (e: ClipboardEvent, record: CountyResearchRecord) => {
    const items = e.clipboardData?.items;
    if (!items) return;

    for (let i = 0; i < items.length; i++) {
      if (items[i].type.indexOf('image') !== -1) {
        e.preventDefault();
        const blob = items[i].getAsFile();
        if (blob) {
          await handleManualScreenshotUpload(record, blob);
        }
        break;
      }
    }
  };

  useEffect(() => {
    if (isReviewMode && reviewIndex !== null) {
      const handlePaste = (e: ClipboardEvent) => {
        handlePasteScreenshot(e, displayRecords[reviewIndex]);
      };
      
      document.addEventListener('paste', handlePaste);
      return () => document.removeEventListener('paste', handlePaste);
    }
  }, [isReviewMode, reviewIndex, displayRecords]);

  const handleCaptureAndExtract = async (record: CountyResearchRecord) => {
    if (!record.pdf_url) {
      toast.error("No document URL available for this record");
      return;
    }

    setCapturingRecordId(record.id);
    toast.info("📸 Capturing screenshot and extracting address...", { duration: 3000 });

    try {
      const { data, error } = await supabase.functions.invoke('capture-and-extract-address', {
        body: {
          recordId: record.id,
          documentUrl: record.pdf_url
        }
      });

      if (error) throw error;

      if (data.success) {
        toast.success(`✅ Address extracted: ${data.address}`, { duration: 5000 });
        loadRecords();
      } else {
        toast.error(data.error || "Failed to extract address");
      }
    } catch (error) {
      console.error("Error capturing and extracting:", error);
      toast.error("Failed to capture screenshot and extract address");
    } finally {
      setCapturingRecordId(null);
    }
  };

  const handleDeleteRecentImport = async () => {
    const recentRecords = displayRecords.filter(r => 
      r.document_number?.startsWith('UNKNOWN-')
    );

    if (recentRecords.length === 0) {
      toast.error("No recent import records found to delete");
      return;
    }

    if (!confirm(`Delete ${recentRecords.length} incorrectly imported records? This cannot be undone.`)) {
      return;
    }

    console.log('Deleting records:', recentRecords.map(r => ({ id: r.id, doc: r.document_number })));

    try {
      const idsToDelete = recentRecords.map(r => r.id);
      console.log(`Attempting to delete ${idsToDelete.length} records`);
      
      // Delete in very small batches to avoid URL length issues
      const batchSize = 5;
      let deletedCount = 0;
      let failedCount = 0;
      
      for (let i = 0; i < idsToDelete.length; i += batchSize) {
        const batch = idsToDelete.slice(i, i + batchSize);
        console.log(`Deleting batch ${Math.floor(i / batchSize) + 1}/${Math.ceil(idsToDelete.length / batchSize)}`);
        
        const { error, count } = await supabase
          .from('county_research')
          .delete({ count: 'exact' })
          .in('id', batch);

        if (error) {
          console.error('Batch delete error:', error);
          failedCount += batch.length;
        } else {
          deletedCount += count || 0;
          console.log(`Batch deleted successfully: ${count} records`);
        }
      }
      
      if (failedCount > 0) {
        toast.error(`Deleted ${deletedCount} records, ${failedCount} failed`);
      } else {
        toast.success(`Successfully deleted ${deletedCount} records`);
      }

      setSelectedIds(new Set());
      await loadRecords();
    } catch (error) {
      console.error("Error deleting records:", error);
      toast.error(`Failed to delete records: ${error.message || 'Unknown error'}`);
    }
  };

  const handleDeleteNonDickinson = async () => {
    const nonDickinsonRecords = displayRecords.filter(r => 
      r.county?.toLowerCase() !== 'dickinson'
    );

    if (nonDickinsonRecords.length === 0) {
      toast.error("No non-Dickinson County records found to delete");
      return;
    }

    if (!confirm(`Delete ${nonDickinsonRecords.length} non-Dickinson County records? This cannot be undone.`)) {
      return;
    }

    try {
      const idsToDelete = nonDickinsonRecords.map(r => r.id);
      
      const batchSize = 10;
      let deletedCount = 0;
      
      for (let i = 0; i < idsToDelete.length; i += batchSize) {
        const batch = idsToDelete.slice(i, i + batchSize);
        
        const { error, count } = await supabase
          .from('county_research')
          .delete({ count: 'exact' })
          .in('id', batch);

        if (error) {
          console.error('Batch delete error:', error);
        } else {
          deletedCount += count || 0;
        }
      }
      
      toast.success(`Successfully deleted ${deletedCount} non-Dickinson County records`);
      setSelectedIds(new Set());
      await loadRecords();
    } catch (error) {
      console.error("Error deleting records:", error);
      toast.error("Failed to delete records");
    }
  };

  const handleBulkDelete = async () => {
    if (selectedIds.size === 0) {
      toast.error("Please select records to delete");
      return;
    }

    if (!confirm(`Are you sure you want to delete ${selectedIds.size} record(s)? They will stay deleted permanently.`)) {
      return;
    }

    try {
      // Soft delete - set deleted_at timestamp
      const { error } = await supabase
        .from('county_research')
        .update({ deleted_at: new Date().toISOString() })
        .in('id', Array.from(selectedIds));

      if (error) throw error;

      toast.success(`Successfully deleted ${selectedIds.size} record(s)`);
      setSelectedIds(new Set());
      await loadRecords();
    } catch (error) {
      console.error("Error deleting records:", error);
      toast.error("Failed to delete records");
    }
  };

  const handleDeleteAllRecords = async () => {
    const totalCount = allRecords.length;
    
    if (totalCount === 0) {
      toast.error("No records to delete");
      return;
    }

    if (!confirm(`⚠️ WARNING: This will permanently delete ALL ${totalCount} records from the county research database. This action CANNOT be undone. Type "DELETE ALL" to confirm.`)) {
      return;
    }

    const secondConfirm = prompt(`Type "DELETE ALL" to confirm deletion of ${totalCount} records:`);
    if (secondConfirm !== "DELETE ALL") {
      toast.error("Deletion cancelled - confirmation text did not match");
      return;
    }

    try {
      setIsLoading(true);
      
      // Delete all records in batches
      const batchSize = 100;
      const allIds = allRecords.map(r => r.id);
      let deletedCount = 0;
      
      for (let i = 0; i < allIds.length; i += batchSize) {
        const batch = allIds.slice(i, i + batchSize);
        
        const { error, count } = await supabase
          .from('county_research')
          .delete({ count: 'exact' })
          .in('id', batch);

        if (error) {
          console.error('Batch delete error:', error);
          throw error;
        } else {
          deletedCount += count || 0;
        }
      }
      
      toast.success(`Successfully deleted all ${deletedCount} records from county research`);
      setSelectedIds(new Set());
      await loadRecords();
    } catch (error) {
      console.error("Error deleting all records:", error);
      toast.error("Failed to delete all records");
    } finally {
      setIsLoading(false);
    }
  };

  const toggleSelectAll = () => {
    if (selectedIds.size === displayRecords.length) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(displayRecords.map(r => r.id)));
    }
  };

  const toggleSelect = (id: string) => {
    const newSelected = new Set(selectedIds);
    if (newSelected.has(id)) {
      newSelected.delete(id);
    } else {
      newSelected.add(id);
    }
    setSelectedIds(newSelected);
  };

  const handleBulkCountyUpdate = async (fromCounty: string, toCounty: string, addressPattern: string) => {
    const recordsToUpdate = allRecords.filter(r => 
      r.county === fromCounty && 
      (r.property_address?.includes(addressPattern) || r.grantee_address?.includes(addressPattern))
    );

    if (recordsToUpdate.length === 0) {
      toast.error(`No ${fromCounty} records found with "${addressPattern}" in address`);
      return;
    }

    if (!confirm(`Update ${recordsToUpdate.length} records from "${fromCounty}" to "${toCounty}"?`)) {
      return;
    }

    try {
      const updates = recordsToUpdate.map(record =>
        supabase
          .from('county_research')
          .update({ county: toCounty, updated_at: new Date().toISOString() })
          .eq('id', record.id)
      );

      await Promise.all(updates);
      toast.success(`Updated ${recordsToUpdate.length} records to ${toCounty}`);
      loadRecords();
    } catch (error: any) {
      console.error('Bulk county update error:', error);
      toast.error("Failed to update counties");
    }
  };

  const handleExportToExcel = () => {
    try {
      const exportData = displayRecords.map(record => ({
        'Document Number': record.document_number,
        'Filing Date': format(new Date(record.filing_date + 'T12:00:00'), 'MM/dd/yyyy'),
        'Seller (Grantor)': record.grantor_name,
        'Seller Address': record.grantor_address || '',
        'Buyer (Grantee)': record.grantee_name,
        'Property Address': record.property_address || '',
        'Loan Amount': record.loan_amount || '',
        'Interest Rate': record.interest_rate ? (record.interest_rate * 100).toFixed(2) + '%' : '',
        'Monthly Payment': record.monthly_payment || '',
        'Term (Months)': record.term_months || '',
        'Balloon Amount': record.balloon_amount || '',
        'Balloon Due Date': record.balloon_due_date ? format(new Date(record.balloon_due_date), 'MM/dd/yyyy') : '',
        'Confidence Score': record.confidence_score || 0,
        'Status': record.is_active ? 'Active' : 'Inactive',
        'Address Needs Review': record.address_needs_review ? 'Yes' : 'No',
        'Notes': record.notes || ''
      }));

      const worksheet = XLSX.utils.json_to_sheet(exportData);
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, 'County Research');
      
      const fileName = `county-research-${format(new Date(), 'yyyy-MM-dd-HHmm')}.xlsx`;
      XLSX.writeFile(workbook, fileName);
      
      toast.success(`Exported ${exportData.length} records to ${fileName}`);
    } catch (error) {
      console.error('Error exporting to Excel:', error);
      toast.error('Failed to export to Excel');
    }
  };

  const handleSaveToGoogleSheets = async () => {
    if (!spreadsheetId.trim()) {
      toast.error("Spreadsheet ID required. Please enter your Google Spreadsheet ID");
      return;
    }

    if (displayRecords.length === 0) {
      toast.error("No data to save");
      return;
    }

    setIsSavingToSheets(true);

    try {
      const { data, error } = await supabase.functions.invoke('save-to-google-sheets', {
        body: {
          records: displayRecords,
          spreadsheetId: spreadsheetId.trim(),
          sheetName: 'County Research',
        },
      });

      if (error) throw error;

      toast.success(`Saved ${displayRecords.length} records to Google Sheets`);
    } catch (error: any) {
      console.error('Error saving to Google Sheets:', error);
      toast.error(error.message || "Failed to save to Google Sheets");
    } finally {
      setIsSavingToSheets(false);
    }
  };

  const startReviewMode = () => {
    // Clear any stale address state first
    setManualAddress('');
    const recordsNeedingReview = displayRecords.filter(r => r.address_needs_review);
    if (recordsNeedingReview.length > 0) {
      const firstIndex = displayRecords.findIndex(r => r.address_needs_review);
      setReviewIndex(firstIndex);
      setIsReviewMode(true);
    } else {
      toast.error("No records need address review");
    }
  };

  const closeReviewMode = () => {
    setIsReviewMode(false);
    setReviewIndex(null);
    setManualAddress('');
  };

  const handleSaveAndNext = async () => {
    if (reviewIndex === null) return;
    
    const currentRecord = displayRecords[reviewIndex];
    
    if (!manualAddress.trim()) {
      toast.error("Please enter an address");
      return;
    }

    try {
      // Find the next record BEFORE updating state
      let nextIndex = -1;
      if (currentRecord.county === 'comal') {
        nextIndex = displayRecords.findIndex((r, idx) => idx > reviewIndex && r.county === 'comal' && !r.grantor_address);
      } else {
        nextIndex = displayRecords.findIndex((r, idx) => idx > reviewIndex && r.address_needs_review);
      }
      
      console.log('Saving address for record:', currentRecord.id, 'Address:', manualAddress);
      
      const { data, error } = await supabase
        .from('county_research')
        .update({
          grantor_address: manualAddress,
          address_needs_review: false,
          address_extraction_method: 'manual',
          address_confidence_score: 100,
          status: 'completed',
          updated_at: new Date().toISOString()
        })
        .eq('id', currentRecord.id)
        .select();

      if (error) {
        console.error('Database update error:', error);
        throw error;
      }
      
      console.log('Database update successful:', data);

      // Update local state immediately without reloading
      const updatedRecords = records.map(r => 
        r.id === currentRecord.id 
          ? {
              ...r,
              grantor_address: manualAddress,
              address_needs_review: false,
              address_extraction_method: 'manual',
              address_confidence_score: 100,
              status: 'completed'
            }
          : r
      );
      setRecords(updatedRecords);
      
      toast.success("Address saved!");
      
      // Clear input immediately to prevent it showing in next record
      setManualAddress('');
      
      // Move to next record AFTER clearing
      if (nextIndex !== -1) {
        setTimeout(() => {
          setReviewIndex(nextIndex);
          const nextRecord = updatedRecords[nextIndex];
          setManualAddress(nextRecord?.grantor_address || '');
        }, 50);
      } else {
        closeReviewMode();
        toast.success("Finished reviewing all records!");
      }
    } catch (error) {
      console.error("Error saving address:", error);
      toast.error("Failed to save address");
    }
  };

  const handleDeleteAndNext = async () => {
    if (reviewIndex === null) return;
    
    const currentRecord = displayRecords[reviewIndex];
    
    try {
      // Soft delete - set deleted_at timestamp
      const { error } = await supabase
        .from('county_research')
        .update({ deleted_at: new Date().toISOString() })
        .eq('id', currentRecord.id);

      if (error) throw error;

      toast.success("Record deleted");
      loadRecords();
      
      // Check if current record was Comal to determine review mode type
      const isComalMode = currentRecord.county === 'comal';
      
      // Move to next record that needs review based on review mode type
      const nextIndex = displayRecords.findIndex((r, idx) => 
        idx >= reviewIndex && 
        r.id !== currentRecord.id && 
        (isComalMode ? (r.county === 'comal' && !r.grantor_address) : r.address_needs_review)
      );
      
      if (nextIndex !== -1 && nextIndex < displayRecords.length - 1) {
        setReviewIndex(nextIndex);
        // Always set from database record, never use stale state
        setManualAddress(displayRecords[nextIndex].grantor_address || '');
      } else {
        closeReviewMode();
      }
    } catch (error) {
      console.error("Error deleting record:", error);
      toast.error("Failed to delete record");
    }
  };

  const handlePrevious = () => {
    if (reviewIndex === null || reviewIndex === 0) return;
    
    // Check if current record is Comal to determine review mode type
    const isComalMode = displayRecords[reviewIndex]?.county === 'comal';
    
    const prevIndex = displayRecords.slice(0, reviewIndex).reverse().findIndex(r => 
      isComalMode ? (r.county === 'comal' && !r.grantor_address) : r.address_needs_review
    );
    
    if (prevIndex !== -1) {
      const actualIndex = reviewIndex - prevIndex - 1;
      setReviewIndex(actualIndex);
      // Always set from database record, never use stale state
      setManualAddress(displayRecords[actualIndex].grantor_address || '');
    }
  };

  const handleNext = () => {
    if (reviewIndex === null) return;
    
    // Check if current record is Comal to determine review mode type
    const isComalMode = displayRecords[reviewIndex]?.county === 'comal';
    
    const nextIndex = displayRecords.findIndex((r, idx) => 
      idx > reviewIndex && (isComalMode ? (r.county === 'comal' && !r.grantor_address) : r.address_needs_review)
    );
    
    if (nextIndex !== -1) {
      setReviewIndex(nextIndex);
      // Always set from database record, never use stale state
      setManualAddress(displayRecords[nextIndex].grantor_address || '');
    }
  };

  // Keyboard shortcuts
  useEffect(() => {
    if (!isReviewMode) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      // Only trigger shortcuts if Ctrl is held AND the key matches
      // This prevents accidental triggers from paste operations
      if (!e.ctrlKey) return;
      
      if (e.key === 'Delete') {
        e.preventDefault();
        handleDeleteAndNext();
      } else if (e.key === 'Enter') {
        e.preventDefault();
        handleSaveAndNext();
      } else if (e.key === 'ArrowLeft') {
        e.preventDefault();
        handlePrevious();
      } else if (e.key === 'ArrowRight') {
        e.preventDefault();
        handleNext();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isReviewMode, reviewIndex, displayRecords, manualAddress]);

  if (isLoading) {
    return (
      <Card>
        <CardContent className="flex flex-col items-center justify-center py-12">
          <RefreshCw className="h-8 w-8 animate-spin text-muted-foreground mb-2" />
          <p className="text-muted-foreground">Loading records...</p>
        </CardContent>
      </Card>
    );
  }

  if (displayRecords.length === 0) {
    return (
      <Card>
        <CardContent className="flex flex-col items-center justify-center py-12">
          <p className="text-muted-foreground">No research records yet. Start capturing contracts from Dickinson County.</p>
        </CardContent>
      </Card>
    );
  }

  const ownerFinanceCount = displayRecords.filter(r => r.is_potential_owner_finance).length;

  return (
    <>
      <Card>
        <CardHeader>
          <div className="flex flex-col gap-4">
            <div className="flex items-center justify-between">
              <div>
                <CardTitle>All Extracted Records</CardTitle>
                <CardDescription>
                  {displayRecords.length} total records, {ownerFinanceCount} potential owner-financed
                  {selectedIds.size > 0 && ` • ${selectedIds.size} selected`}
                </CardDescription>
              </div>
            </div>
            <div className="flex gap-2 items-center flex-wrap">
              <Select value={countyFilter} onValueChange={setCountyFilter}>
                <SelectTrigger className="w-[180px]">
                  <SelectValue placeholder="Filter by county" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Counties</SelectItem>
                  {counties.map(county => (
                    <SelectItem key={county} value={county}>
                      {county.charAt(0).toUpperCase() + county.slice(1)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger className="w-[200px]">
                  <SelectValue placeholder="Filter by status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Statuses</SelectItem>
                  <SelectItem value="melissa">Ready for Melissa Data ({recordsReadyForMelissa.length})</SelectItem>
                  <SelectItem value="needs_review">Needs Review ({recordsNeedingReview.length})</SelectItem>
                </SelectContent>
              </Select>
              <Input
                placeholder="Google Spreadsheet ID (paste from URL)"
                value={spreadsheetId}
                onChange={(e) => setSpreadsheetId(e.target.value)}
                className="w-80"
              />
              {recordsNeedingReview.length > 0 && (
                <Button
                  variant="default"
                  size="sm"
                  onClick={startReviewMode}
                >
                  Start Review Mode ({recordsNeedingReview.length})
                </Button>
              )}
              {countyFilter === 'comal' && comalRecordsNeedingReview.length > 0 && (
                <Button
                  variant="default"
                  size="sm"
                  onClick={() => {
                    // Clear any stale address state first
                    setManualAddress('');
                    const firstIndex = displayRecords.findIndex(r => r.county === 'comal' && !r.grantor_address);
                    if (firstIndex !== -1) {
                      setReviewIndex(firstIndex);
                      setIsReviewMode(true);
                    }
                  }}
                >
                  Review Comal Addresses ({comalRecordsNeedingReview.length})
                </Button>
              )}
              {countyFilter === 'comal' && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={async () => {
                    if (!confirm('This will clear ALL addresses for Comal County records so you can re-enter them. Continue?')) {
                      return;
                    }
                    try {
                      // Close review mode first to prevent stale data
                      closeReviewMode();
                      
                      const { data, error } = await supabase.functions.invoke('clear-comal-addresses');
                      if (error) throw error;
                      toast.success(`Cleared addresses for ${data.recordsUpdated} records`);
                      
                      // Await loadRecords and clear address state
                      await loadRecords();
                      setManualAddress('');
                    } catch (error) {
                      console.error('Error clearing addresses:', error);
                      toast.error('Failed to clear addresses');
                    }
                  }}
                  className="text-orange-600 hover:text-orange-700"
                >
                  Clear All Comal Addresses
                </Button>
              )}
              <Button
                variant="outline"
                size="sm"
                onClick={handleExportToExcel}
              >
                <Download className="mr-2 h-4 w-4" />
                Export to Excel
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={handleSaveToGoogleSheets}
                disabled={isSavingToSheets || !spreadsheetId.trim()}
                title={!spreadsheetId.trim() ? "Enter Spreadsheet ID first" : "Append records to Google Sheets"}
              >
                <Upload className="mr-2 h-4 w-4" />
                {isSavingToSheets ? "Saving..." : "Save to Google Sheets"}
              </Button>
              <Button
                variant="destructive"
                size="sm"
                onClick={handleDeleteRecentImport}
              >
                <Trash2 className="mr-2 h-4 w-4" />
                Delete Failed Import
              </Button>
              <Button
                variant="destructive"
                size="sm"
                onClick={handleDeleteNonDickinson}
              >
                <Trash2 className="mr-2 h-4 w-4" />
                Delete Non-Dickinson
              </Button>
              <Button
                variant="destructive"
                size="sm"
                onClick={handleDeleteAllRecords}
              >
                <Trash2 className="mr-2 h-4 w-4" />
                Delete ALL Records
              </Button>
              {selectedIds.size > 0 && (
                <Button
                  variant="destructive"
                  size="sm"
                  onClick={handleBulkDelete}
                >
                  <Trash2 className="mr-2 h-4 w-4" />
                  Delete Selected ({selectedIds.size})
                </Button>
              )}
              <Button
                variant="outline"
                size="sm"
                onClick={() => handleBulkCountyUpdate('randall', 'madison', 'London, Ohio')}
                className="text-blue-600 hover:text-blue-700"
              >
                Fix Madison Counties
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={loadRecords}
              >
                <RefreshCw className="mr-2 h-4 w-4" />
                Refresh
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-[50px]">
                  <Checkbox
                    checked={selectedIds.size === displayRecords.length && displayRecords.length > 0}
                    onCheckedChange={toggleSelectAll}
                  />
                </TableHead>
                <TableHead>Status</TableHead>
                <TableHead>County</TableHead>
                <TableHead>Filing Date</TableHead>
                <TableHead>Doc #</TableHead>
                <TableHead>Seller (Grantor)</TableHead>
                <TableHead>Buyer (Grantee)</TableHead>
                <TableHead>Property</TableHead>
                <TableHead>Loan Amount</TableHead>
                <TableHead>Confidence</TableHead>
                <TableHead>Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {displayRecords.map((record) => (
                <>
                  <TableRow 
                    key={record.id} 
                    className="cursor-pointer hover:bg-muted/50" 
                    onClick={() => {
                      const newExpanded = new Set(expandedRows);
                      if (newExpanded.has(record.id)) {
                        newExpanded.delete(record.id);
                      } else {
                        newExpanded.add(record.id);
                      }
                      setExpandedRows(newExpanded);
                    }}
                  >
                    <TableCell onClick={(e) => e.stopPropagation()} className="w-12">
                      <div className="flex items-center gap-2">
                        {expandedRows.has(record.id) ? (
                          <ChevronDown className="h-4 w-4 text-muted-foreground" />
                        ) : (
                          <ChevronRight className="h-4 w-4 text-muted-foreground" />
                        )}
                        <Checkbox
                          checked={selectedIds.has(record.id)}
                          onCheckedChange={() => toggleSelect(record.id)}
                        />
                      </div>
                    </TableCell>
                  <TableCell>
                    {record.status === 'completed' ? (
                      <Badge className="bg-green-600 hover:bg-green-700">Completed</Badge>
                    ) : record.is_active ? (
                      <Badge variant="default">Active</Badge>
                    ) : (
                      <Badge variant="secondary">Inactive</Badge>
                    )}
                  </TableCell>
                  <TableCell>
                    <Badge variant="outline" className="capitalize">
                      {record.county || 'Unknown'}
                    </Badge>
                  </TableCell>
                  <TableCell>{format(new Date(record.filing_date + 'T12:00:00'), 'MM/dd/yyyy')}</TableCell>
                  <TableCell className="font-mono text-sm">{record.document_number}</TableCell>
                  <TableCell>
                    <div>
                      <p className="font-medium">{record.grantor_name}</p>
                      {record.address_needs_review ? (
                        <Badge variant="outline" className="text-xs">Address needed</Badge>
                      ) : record.grantor_address ? (
                        <p className="text-xs text-muted-foreground truncate max-w-[200px]">
                          {record.grantor_address}
                        </p>
                      ) : null}
                    </div>
                  </TableCell>
                  <TableCell>{record.grantee_name}</TableCell>
                  <TableCell className="max-w-[200px] truncate">{record.property_address || 'N/A'}</TableCell>
                  <TableCell>{record.loan_amount ? formatCurrency(record.loan_amount) : 'N/A'}</TableCell>
                  <TableCell>
                    <Badge 
                      variant={
                        (record.confidence_score || 0) > 80 ? 'default' : 
                        (record.confidence_score || 0) > 60 ? 'secondary' : 
                        'outline'
                      }
                    >
                      {record.confidence_score || 0}%
                    </Badge>
                  </TableCell>
                  <TableCell onClick={(e) => e.stopPropagation()}>
                    <div className="flex gap-2">
                      {record.address_needs_review ? (
                        <Button
                          size="sm"
                          variant="default"
                          onClick={() => setSelectedRecord(record)}
                        >
                          Add Address
                        </Button>
                      ) : (
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => setSelectedRecord(record)}
                        >
                          <Eye className="h-4 w-4 mr-1" />
                          View
                        </Button>
                      )}
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => {
                          const input = document.createElement('input');
                          input.type = 'file';
                          input.accept = 'image/*';
                          input.onchange = (e) => {
                            const file = (e.target as HTMLInputElement).files?.[0];
                            if (file) {
                              handleManualScreenshotUpload(record, file);
                            }
                          };
                          input.click();
                        }}
                        disabled={uploadingRecordId === record.id}
                        title="Upload screenshot to extract seller's address"
                      >
                        {uploadingRecordId === record.id ? (
                          <RefreshCw className="h-4 w-4 animate-spin" />
                        ) : (
                          <>
                            <Upload className="h-4 w-4 mr-1" />
                            Upload
                          </>
                        )}
                      </Button>
                      {record.pdf_url && (
                        <>
                          <Button
                            size="sm"
                            variant="secondary"
                            onClick={() => handleCaptureAndExtract(record)}
                            disabled={capturingRecordId === record.id}
                            title="Capture screenshot and extract address with AI"
                          >
                            {capturingRecordId === record.id ? (
                              <RefreshCw className="h-4 w-4 animate-spin" />
                            ) : (
                              <>
                                <Camera className="h-4 w-4 mr-1" />
                                Auto
                              </>
                            )}
                          </Button>
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => window.open(record.pdf_url, '_blank')}
                          >
                            <ExternalLink className="h-4 w-4" />
                          </Button>
                        </>
                      )}
                      <Button
                        size="sm"
                        variant="destructive"
                        onClick={() => handleDelete(record.id)}
                        title="Delete record"
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
                {expandedRows.has(record.id) && (
                  <TableRow key={`${record.id}-expanded`}>
                    <TableCell colSpan={11} className="bg-muted/30 p-4">
                      <div className="grid grid-cols-2 gap-4 text-sm">
                        <div>
                          <p className="font-semibold text-muted-foreground mb-1">Seller Address</p>
                          <p>{record.grantor_address || 'Not available'}</p>
                        </div>
                        <div>
                          <p className="font-semibold text-muted-foreground mb-1">Buyer Address</p>
                          <p>{record.grantee_address || 'Not available'}</p>
                        </div>
                        <div>
                          <p className="font-semibold text-muted-foreground mb-1">Interest Rate</p>
                          <p>{record.interest_rate ? `${(record.interest_rate * 100).toFixed(2)}%` : 'N/A'}</p>
                        </div>
                        <div>
                          <p className="font-semibold text-muted-foreground mb-1">Monthly Payment</p>
                          <p>{record.monthly_payment ? formatCurrency(record.monthly_payment) : 'N/A'}</p>
                        </div>
                        <div>
                          <p className="font-semibold text-muted-foreground mb-1">Term</p>
                          <p>{record.term_months ? `${record.term_months} months` : 'N/A'}</p>
                        </div>
                        <div>
                          <p className="font-semibold text-muted-foreground mb-1">Balloon Payment</p>
                          <p>{record.balloon_amount ? formatCurrency(record.balloon_amount) : 'None'}</p>
                        </div>
                        {record.balloon_due_date && (
                          <div>
                            <p className="font-semibold text-muted-foreground mb-1">Balloon Due Date</p>
                            <p>{format(new Date(record.balloon_due_date), 'MM/dd/yyyy')}</p>
                          </div>
                        )}
                        {record.legal_description && (
                          <div className="col-span-2">
                            <p className="font-semibold text-muted-foreground mb-1">Legal Description</p>
                            <p className="text-xs">{record.legal_description}</p>
                          </div>
                        )}
                        {record.notes && (
                          <div className="col-span-2">
                            <p className="font-semibold text-muted-foreground mb-1">Notes</p>
                            <p className="text-xs">{record.notes}</p>
                          </div>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                )}
              </>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {selectedRecord && (
        <RecordDetailModal
          record={selectedRecord}
          open={selectedRecord !== null}
          onClose={() => setSelectedRecord(null)}
          onUpdate={loadRecords}
        />
      )}

      {/* Review Mode Dialog */}
      {isReviewMode && reviewIndex !== null && displayRecords[reviewIndex] && (
        <Dialog open={isReviewMode} onOpenChange={(open) => !open && closeReviewMode()}>
          <DialogContent className="max-w-[95vw] w-full max-h-[95vh] overflow-hidden flex flex-col">
            <DialogHeader>
              <DialogTitle className="flex flex-col gap-3">
                <div className="flex items-center justify-between">
                  <span className="text-2xl font-bold">
                    Record {currentReviewPosition} of {activeReviewList.length}
                  </span>
                  <div className="flex gap-2">
                    <Button
                      onClick={handlePrevious}
                      disabled={currentReviewPosition === 1}
                      variant="outline"
                      size="sm"
                    >
                      <ChevronLeft className="h-4 w-4" />
                      Previous
                    </Button>
                    <Button
                      onClick={handleNext}
                      disabled={currentReviewPosition >= activeReviewList.length}
                      variant="outline"
                      size="sm"
                    >
                      Next
                      <ChevronRight className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
                <div className="space-y-2">
                  <div className="flex items-center justify-between text-sm text-muted-foreground">
                    <span>{activeReviewList.length - currentReviewPosition} records remaining</span>
                    <span>{Math.round((currentReviewPosition / activeReviewList.length) * 100)}% complete</span>
                  </div>
                  <div className="w-full bg-secondary h-2 rounded-full overflow-hidden">
                    <div 
                      className="bg-primary h-full transition-all duration-300"
                      style={{ width: `${(currentReviewPosition / activeReviewList.length) * 100}%` }}
                    />
                  </div>
                </div>
              </DialogTitle>
            </DialogHeader>

            <div className="flex-1 grid grid-cols-2 gap-6 overflow-hidden">
              {/* Left side - Raw Text / Record Details */}
              <div className="flex flex-col gap-3 overflow-hidden border-r pr-6">
                <div className="flex items-center gap-2">
                  <h4 className="font-semibold text-lg">📋 Record Details</h4>
                  <Badge variant="secondary">Copy from here</Badge>
                </div>
                
                <div className="flex-1 overflow-y-auto space-y-4">
                  <div className="grid grid-cols-2 gap-4 p-4 bg-muted rounded-lg">
                    <div>
                      <label className="text-sm font-medium text-muted-foreground">Document Number</label>
                      <button
                        onClick={() => {
                          navigator.clipboard.writeText(displayRecords[reviewIndex].document_number);
                          toast.success("Document number copied!");
                        }}
                        className="text-lg font-mono hover:bg-accent hover:text-accent-foreground px-2 py-1 -ml-2 rounded transition-colors cursor-pointer"
                        title="Click to copy"
                      >
                        {displayRecords[reviewIndex].document_number}
                      </button>
                    </div>
                    <div>
                      <label className="text-sm font-medium text-muted-foreground">Filing Date</label>
                      <p className="text-lg">{format(new Date(displayRecords[reviewIndex].filing_date + 'T12:00:00'), 'MM/dd/yyyy')}</p>
                    </div>
                    <div className="col-span-2">
                      <label className="text-sm font-medium text-muted-foreground">Buyer/Borrower (Grantor)</label>
                      <p className="text-lg font-medium">{displayRecords[reviewIndex].grantor_name}</p>
                    </div>
                    <div className="col-span-2">
                      <label className="text-sm font-medium text-muted-foreground">Trustee (Grantee)</label>
                      <p className="text-lg font-medium">{displayRecords[reviewIndex].grantee_name}</p>
                    </div>
                    <div className="col-span-2 bg-red-50 dark:bg-red-950/20 border-2 border-red-300 dark:border-red-800 p-3 rounded">
                      <label className="text-sm font-bold text-red-700 dark:text-red-400">⚠️ NEED: BENEFICIARY (Seller/Lender)</label>
                      <p className="text-xs text-red-600 dark:text-red-300 mt-1">Look for "BENEFICIARY" section in the deed - this is the seller's address you need to enter!</p>
                    </div>
                    {displayRecords[reviewIndex].property_address && (
                      <div className="col-span-2">
                        <label className="text-sm font-medium text-muted-foreground">Property Address</label>
                        <p className="text-sm">{displayRecords[reviewIndex].property_address}</p>
                      </div>
                    )}
                  </div>

                  {/* Raw Data Section */}
                  {displayRecords[reviewIndex].raw_data && (
                    <div className="space-y-2">
                      <h4 className="font-semibold">Raw County Data</h4>
                      <Textarea
                        value={JSON.stringify(displayRecords[reviewIndex].raw_data, null, 2)}
                        className="font-mono text-xs resize-none bg-muted min-h-[200px]"
                        readOnly
                      />
                    </div>
                  )}

                  {displayRecords[reviewIndex].legal_description && (
                    <div className="space-y-2">
                      <h4 className="font-semibold">Legal Description</h4>
                      <Textarea
                        value={displayRecords[reviewIndex].legal_description || ''}
                        className="text-xs resize-none bg-muted min-h-[100px]"
                        readOnly
                      />
                    </div>
                  )}

                  {displayRecords[reviewIndex].pdf_url && (
                    <div className="space-y-2">
                      <h4 className="font-semibold">Document URL</h4>
                      <div className="flex items-center gap-2">
                        <Input
                          value={displayRecords[reviewIndex].pdf_url || ''}
                          className="text-xs font-mono"
                          readOnly
                        />
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => window.open(displayRecords[reviewIndex].pdf_url, '_blank')}
                        >
                          <ExternalLink className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* Right side - Editable Address */}
              <div className="flex flex-col gap-4 overflow-y-auto">
                <div className="flex items-center gap-2">
                  <h4 className="font-semibold text-lg">✏️ Enter Address</h4>
                  <Badge variant="secondary">Paste here</Badge>
                </div>

                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <h3 className="font-semibold text-red-600 dark:text-red-400">BENEFICIARY's Address (NOT Trustee!)</h3>
                    <Button
                      type="button"
                      size="sm"
                      variant="outline"
                      onClick={() => {
                        const input = document.createElement('input');
                        input.type = 'file';
                        input.accept = 'image/*';
                        input.onchange = (e) => {
                          const file = (e.target as HTMLInputElement).files?.[0];
                          if (file && reviewIndex !== null) {
                            handleManualScreenshotUpload(displayRecords[reviewIndex], file);
                          }
                        };
                        input.click();
                      }}
                      disabled={uploadingRecordId !== null}
                    >
                      {uploadingRecordId !== null ? (
                        <RefreshCw className="mr-2 h-4 w-4 animate-spin" />
                      ) : (
                        <Upload className="mr-2 h-4 w-4" />
                      )}
                      Upload Screenshot
                    </Button>
                  </div>
                  
                  <div className="text-xs bg-red-50 dark:bg-red-950/20 border-2 border-red-300 dark:border-red-800 p-4 rounded">
                    <p className="font-bold text-red-700 dark:text-red-400 mb-2">⚠️ CRITICAL: Find the BENEFICIARY section!</p>
                    <p className="text-red-600 dark:text-red-300">In a Deed of Trust:</p>
                    <ul className="ml-4 mt-1 space-y-1 text-red-600 dark:text-red-300">
                      <li>• <strong>BENEFICIARY</strong> = SELLER/LENDER (this is what you need!)</li>
                      <li>• Grantor = Buyer/Borrower (skip this)</li>
                      <li>• Grantee = Trustee (skip this)</li>
                    </ul>
                    <p className="mt-2 text-xs">💡 Screenshot the document and paste (Ctrl+V) to auto-extract</p>
                  </div>
                  
                  <div>
                    <Textarea
                      placeholder="Enter BENEFICIARY's mailing address (e.g., 123 Main St, City, State ZIP) or paste screenshot (Ctrl+V)"
                      value={manualAddress}
                      onChange={(e) => setManualAddress(e.target.value)}
                      rows={6}
                      className="mt-1 text-base"
                      autoFocus
                    />
                  </div>

                  {displayRecords[reviewIndex].grantor_address && (
                    <div className="text-xs text-muted-foreground bg-blue-50 dark:bg-blue-950 p-3 rounded border border-blue-200 dark:border-blue-800">
                      <strong>Current saved address:</strong>
                      <p className="mt-1">{displayRecords[reviewIndex].grantor_address}</p>
                    </div>
                  )}
                </div>

                <div className="flex gap-3 pt-4 border-t mt-auto">
                  <Button
                    onClick={handleDeleteAndNext}
                    variant="destructive"
                    className="flex-1"
                  >
                    <X className="mr-2 h-4 w-4" />
                    Delete & Next (Ctrl+Delete)
                  </Button>
                  <Button
                    onClick={handleSaveAndNext}
                    variant="default"
                    className="flex-1"
                  >
                    <Save className="mr-2 h-4 w-4" />
                    Save & Next (Ctrl+Enter)
                  </Button>
                </div>

                <div className="text-xs text-muted-foreground space-y-1 pb-4">
                  <p><kbd className="px-2 py-1 bg-muted rounded">Ctrl + Enter</kbd> Save & Next</p>
                  <p><kbd className="px-2 py-1 bg-muted rounded">Ctrl + Delete</kbd> Delete & Next</p>
                  <p><kbd className="px-2 py-1 bg-muted rounded">Ctrl + ←/→</kbd> Navigate records</p>
                </div>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      )}
    </>
  );
}
